/** @file Asks the Worker who is signed in (GET /auth/me) and loads UI settings for a signed-in user. */
import type { Actor } from '@pose2d/api/types';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { ApiClient } from '../../api/createApiClient';
import type { UiSettings } from '../../api/apiTypes';
import { sessionStore } from '../../stores/sessionStore';
import { settingsStore } from '../../stores/settingsStore';
import { toSettingsSlice } from '../settings/toSettingsSlice';

/** Null only when the stored rows are unreadable (VALIDATION): the defaults stay and the next PUT rewrites them; any other failure rethrows. */
function readSettings(client: Pick<ApiClient, 'getUiSettings'>): Promise<UiSettings | null> {
  return client.getUiSettings().catch((e: unknown) => {
    if (toPose2DError(e)?.code === 'VALIDATION') return null;
    throw e;
  });
}

/** UNAUTHORIZED from /auth/me means signed out; any other failure (also a settings read) rethrows and leaves the session as it was. */
export async function checkSession(client: Pick<ApiClient, 'me' | 'getUiSettings'>): Promise<void> {
  let actor: Actor;
  try {
    ({ actor } = await client.me());
  } catch (e) {
    if (toPose2DError(e)?.code !== 'UNAUTHORIZED') throw e;
    sessionStore.setState({ status: 'anonymous', actor: null });
    return;
  }
  const ui = await readSettings(client);
  if (ui !== null) settingsStore.setState(toSettingsSlice(ui));
  sessionStore.setState({ status: 'signedIn', actor });
}
